/**
 * `<HoursPillSection/>` — member-side hours-vs-contract pill in the
 * persistent right panel (#212; sub-issue of #155).
 *
 * Closes the wireframe-alignment gap identified by the Phase-1 audit
 * on #210: the wireframe at `docs/research/ui-wireframes-page-by-page.md`
 * lines 486-504 specs a `⌚ Hours / 44h of 40 contracted (+4) / See
 * detail →` row in `<MemberPersistentPanel/>` — the load-bearing BP-2
 * worker-friendly signal letting the worker see their cycle hours-vs-
 * contract delta ambiently, without navigating to `/me/hours`.
 *
 * **Data source.** `useMeHoursVsContract()` — same hook the detail
 * page reads. Cache-key `["me-hours-vs-contract", accountId]` is
 * shared, so opening the panel doesn't double-fetch when the worker
 * clicks through to `/me/hours`.
 *
 * **Status nouns + tones** are imported from the hook module — the
 * pill labels the row identically to `/me/hours` and the lead-side
 * `/cycles/:id/hours`. That symmetry IS the trust handshake (BP-1)
 * per `docs/design/lead-hours-vs-contract-plan.md` §3.6.
 *
 * **Self-hide states.** Returns `null` on fetch error,
 * `state="no_membership"` (no team yet), `state="no_data"` (no
 * published cycle yet). The detail page handles these with `<EmptyState/>`
 * blocks; the panel pill is supposed to be ambient context, so an
 * empty pill is noisier than no pill.
 *
 * **Focal-component discipline** per `feedback_ui_section_focal_component.md`:
 * the focal element is the delta badge — the verdict in one glance.
 * The "44h of 40" line is supporting context. The "See detail →" link
 * is calm chrome.
 *
 * **v0.1 SPA-only synthesis** per IA-rework doc §6 PR 9 — reuses the
 * existing endpoint, no new route. The broader #155 work (multi-cycle
 * history chart, threshold-crossing audit log, `/me/export` extension)
 * stays open as separate scope.
 */
import { Clock } from "lucide-react";

import {
  ME_HOURS_STATUS_LABEL,
  ME_HOURS_STATUS_TONE,
  useMeHoursVsContract,
} from "../api/useMeHoursVsContract";
import { useTeamFeature } from "../api/useTeamFeature";
import { AmbientPill } from "./AmbientPill";
import { Badge } from "./ui";

function formatHoursWeek(value: number | null | undefined): string {
  if (value == null) return "—";
  return value.toFixed(1);
}

function formatPillDelta(delta: number | null | undefined): string {
  if (delta == null) return "—";
  const rounded = Math.abs(delta) < 0.05 ? 0 : delta;
  if (rounded === 0) return "0 hrs";
  const sign = rounded > 0 ? "+" : "-";
  return `${sign}${Math.abs(rounded).toFixed(1)} hrs`;
}

export function HoursPillSection() {
  const hoursQuery = useMeHoursVsContract();
  // #193 — gate on `hours_balance_member` (registry §155). The
  // team_id only becomes known after the hours fetch resolves
  // (`scope.team_id`), so the gate sits below the fetch rather than
  // above the section in the panel parent. `undefined` = loading;
  // `false` = component disabled by the team's admin OR fetch errored
  // — the optimistic-render branch in `<ComponentGate>` keeps loading
  // calm. Hidden gives the section its existing self-hide treatment.
  const memberHoursEnabled = useTeamFeature(
    hoursQuery.data?.scope?.team_id,
    "hours_balance_member",
  );

  // Self-hide on error or transient empty states. The detail page
  // owns the "no team yet" / "no published cycle yet" empty-state
  // copy — duplicating it in the panel pill would just clutter.
  if (hoursQuery.isError) return null;
  if (hoursQuery.data?.state === "no_membership") return null;
  if (hoursQuery.data?.state === "no_data") return null;
  if (hoursQuery.data?.scope?.team_id && memberHoursEnabled === false) return null;

  return (
    <AmbientPill
      icon={Clock}
      title="Hours"
      ariaLabel="Your hours"
      detailTo="/me/hours"
      detailTestId="hours-pill-detail-link"
      testId="hours-pill-section"
      isLoading={hoursQuery.isLoading}
      skeletonClassName="h-10"
      skeletonTestId="hours-pill-loading"
    >
      <PillBody data={hoursQuery.data} />
    </AmbientPill>
  );
}

interface PillBodyProps {
  data: ReturnType<typeof useMeHoursVsContract>["data"];
}

function PillBody({ data }: PillBodyProps) {
  if (!data) return null;
  const { row, state } = data;

  // `state="no_cap_set"` arrives as ready-shaped (with row) but the
  // worker's lead hasn't set their max yet. Render a calm "No max set"
  // line — same noun as the detail page, so the worker recognises the
  // shape when they click through.
  if (state === "no_cap_set" || row?.status === "no_cap_set") {
    return (
      <div className="flex flex-col gap-1" data-testid="hours-pill-no-cap-set">
        <span className="text-xs text-fg-muted">No max set yet</span>
        <Badge tone="neutral" className="self-start" data-testid="hours-pill-status-badge">
          {ME_HOURS_STATUS_LABEL.no_cap_set}
        </Badge>
      </div>
    );
  }

  if (!row) return null;
  const status = row.status;
  const tone = ME_HOURS_STATUS_TONE[status];
  const label = ME_HOURS_STATUS_LABEL[status];
  const cap = row.effective_cap_hours_per_week;
  const delta = row.delta_per_week_hrs;

  return (
    <div className="flex flex-col gap-1.5" data-testid="hours-pill-ready">
      {/* Body row — supporting context. The scheduled / contract numbers
          let the worker triangulate the delta, but they're calmer
          weight than the badge. */}
      <div className="flex items-baseline gap-1.5 text-xs text-fg-muted">
        <span className="text-sm font-medium tabular-nums text-fg">
          {formatHoursWeek(row.scheduled_hours_per_week_avg)}h
        </span>
        <span>
          of {formatHoursWeek(cap)} contract
          {cap == null ? "" : "/wk"}
        </span>
      </div>
      {/* Focal — status verdict + signed delta in one badge. */}
      <Badge tone={tone} className="self-start" data-testid="hours-pill-status-badge">
        <span>{label}</span>
        <span className="text-fg-faint" aria-hidden="true">
          ·
        </span>
        <span className="tabular-nums" data-testid="hours-pill-delta">
          {formatPillDelta(delta)}
        </span>
      </Badge>
    </div>
  );
}
